/*
 * Product protection popup
 * ------------------------------------------------------------
 * Shown when the merchant has enabled the popup on the
 * "Product protection popup" section AND the product being added carries the
 * `warranty_product` tag. The product-form add-to-cart submit is intercepted
 * (see product-form.js) and open() is called.
 *
 * Plan prices are computed live as a percentage of the currently
 * selected main-product variant price (data-percentage per plan box),
 * and recalculated on every variant change.
 *
 * - "I don't want protection" (decline) -> normal add-to-cart of the
 *   main product only, with no extra line item properties.
 * - "Protect my purchase" (confirm) -> adds the main product AND the
 *   selected protection variant in one request, cross-linking the two
 *   with line item properties.
 */
if (!customElements.get('protection-popup')) {
  customElements.define(
    'protection-popup',
    class ProtectionPopup extends HTMLElement {
      constructor() {
        super();
        this.activeForm = null;
        this.boundKeydown = this.onKeydown.bind(this);

        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.mainPrice = parseInt(this.dataset.mainPrice, 10) || 0;
        this.currentVariant = {
          id: this.dataset.mainVariantId || '',
          title: this.dataset.mainVariantTitle || '',
          price: this.mainPrice,
        };
      }

      connectedCallback() {
        // Guard against re-entry: repairDom() can move this element in the DOM,
        // which re-fires connectedCallback synchronously.
        if (this._repairing) return;
        this._repairing = true;
        this.repairDom();
        if (!this._initialized) {
          this._initialized = true;
          this.init();
        }
        this._repairing = false;
      }

      // Self-heal for a DOM-splitting bug seen on fitment products. The YMM app
      // emits a stray/unclosed <a> (a "compatible vehicle" link) that ends up
      // wrapping this section. Because our modal markup contains its own <a>
      // (the details link), the browser's nested-anchor repair (the HTML
      // "adoption agency" algorithm) SPLITS this element: the modal is detached
      // as a sibling and this <protection-popup> is left EMPTY inside the stray
      // <a>. Result: opening the popup set [open] on an empty element, so nothing
      // showed. Heal it — lift ourselves out of the stray <a>, then pull the
      // detached modal back inside so `.protection-popup[open] .modal` can reveal it.
      repairDom() {
        const anchor = this.closest('a');
        if (anchor && anchor.parentElement) anchor.after(this);

        if (!this.querySelector('.protection-popup__modal')) {
          const scope = this.closest('.shopify-section') || this.parentElement || document;
          const modal = scope.querySelector('.protection-popup__modal');
          if (modal) this.appendChild(modal);
        }
        this.modal = this.querySelector('.protection-popup__modal');
        // A freshly-(re)adopted modal must be fully inert unless we're actually
        // open — otherwise it can linger as an invisible overlay over the page.
        if (this.modal && !this.hasAttribute('open')) {
          this.modal.classList.remove('is-open');
          this.modal.hidden = true;
        }
      }

      init() {
        // Identify the main product section so we only react to its variant changes.
        const mainForm = document.querySelector('product-form');
        this.mainSectionId = mainForm ? mainForm.dataset.sectionId : null;

        this.bindEvents();
        this.updatePrices();

        // Ensure exactly one plan is selected by default.
        if (this.plans.length && !this.querySelector('[data-protection-plan].is-selected')) {
          this.selectPlan(this.plans[0]);
        }
      }

      bindEvents() {
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-protection-close]')) {
            event.preventDefault();
            this.close();
            return;
          }
          // Details & FAQ is a <button> (not an <a>, to avoid the anchor-nesting
          // split described in repairDom); open its URL manually.
          const detailsBtn = event.target.closest('[data-protection-details]');
          if (detailsBtn) {
            event.preventDefault();
            const href = detailsBtn.dataset.href;
            if (href && href !== '#') window.open(href, '_blank', 'noopener');
            return;
          }
          const planEl = event.target.closest('[data-protection-plan]');
          if (planEl) {
            this.selectPlan(planEl);
            return;
          }
          if (event.target.closest('[data-protection-decline]')) {
            event.preventDefault();
            this.proceed();
            return;
          }
          if (event.target.closest('[data-protection-confirm]')) {
            event.preventDefault();
            this.addWithProtection();
            return;
          }
          // Click on the backdrop (the overlay itself, outside the card) closes it.
          if (this.modal && event.target === this.modal) {
            this.close();
          }
        });

        // On the product page, keep the <product-form>'s ATC data in sync with the
        // currently-selected variant, so opening the popup uses the right price/title.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event && event.data && event.data.variant;
            if (!variant) return;
            const pf = document.querySelector('product-form');
            if (pf) {
              pf.setAttribute('data-atc-price', variant.price);
              pf.setAttribute('data-atc-variant-title', variant.title || '');
            }
          });
        }
      }

      get enabled() {
        return this.dataset.enabled === 'true';
      }

      get plans() {
        return Array.from(this.querySelectorAll('[data-protection-plan]'));
      }

      selectPlan(planEl) {
        this.plans.forEach((plan) => {
          const isTarget = plan === planEl;
          plan.classList.toggle('is-selected', isTarget);
          const input = plan.querySelector('input[type="radio"]');
          if (input) input.checked = isTarget;
        });
      }

      get selectedPlan() {
        return this.querySelector('[data-protection-plan].is-selected') || this.plans[0] || null;
      }

      updatePrices() {
        this.plans.forEach((plan) => {
          const pct = parseFloat(plan.dataset.percentage) || 0;
          const priceEl = plan.querySelector('[data-protection-plan-price]');
          if (!priceEl) return;
          const cents = Math.round((this.mainPrice * pct) / 100);
          const formatted = this.formatMoney(cents);
          // Only touch the DOM when the value actually changed. Rewriting
          // identical markup still counts as a DOM mutation, which wakes any
          // page-wide MutationObserver (e.g. the YMM fitment app on parts that
          // carry vehicle data). On those products the observer re-renders,
          // that mutation fires again, and the page spins into a freeze on
          // Add-to-Cart. Skipping no-op writes removes that trigger and also
          // breaks any echo loop back through the variantChange handler.
          if (priceEl.innerHTML !== formatted) priceEl.innerHTML = formatted;
        });
      }

      // Read a data-atc-* value from the submitted form or its <product-form> host.
      atcData(form, name) {
        if (form && form.hasAttribute && form.hasAttribute(name)) return form.getAttribute(name);
        const host = form && form.closest ? form.closest('product-form') : null;
        return host ? host.getAttribute(name) : null;
      }

      // Open the popup for whichever product's Add-to-Cart form was submitted —
      // works site-wide (product page, product cards, quick-add). The product's
      // variant / price / title come from the form (data-atc-* + the id input).
      openFor(form) {
        this.activeForm = form || null;

        const idInput = form ? form.querySelector('[name="id"]') : null;
        this.activeVariantId = idInput ? idInput.value : '';
        this.mainPrice = parseInt(this.atcData(form, 'data-atc-price'), 10) || this.mainPrice || 0;
        this.activeTitle = this.atcData(form, 'data-atc-title') || '';
        this.currentVariant = {
          id: this.activeVariantId,
          title: this.atcData(form, 'data-atc-variant-title') || '',
          price: this.mainPrice,
        };

        // Reveal the overlay. Visibility is driven by `.is-open` (+ removing the
        // `hidden` attribute) on the modal ELEMENT itself, so it works even if
        // the YMM split detached the modal from this host. We keep the host
        // [open] attribute too for back-compat, but nothing renders off it.
        // We do NOT re-parent to <body> — that tripped the YMM app's observers.
        this.setAttribute('open', '');
        if (this.modal) {
          this.modal.hidden = false;
          this.modal.classList.add('is-open');
        }
        this.clearError();

        // Lock background scroll WITHOUT changing the layout width. Plain
        // `overflow:hidden` removes the scrollbar, which shifts the viewport width
        // and retriggers ResizeObserver-based widgets (the YMM bar / sticky header)
        // in a loop — that was what froze the page. Reserving padding equal to the
        // scrollbar width keeps every element's box the same width, so nothing
        // resizes and no observer loop fires.
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
          document.documentElement.style.setProperty('--pp-scrollbar-width', scrollbarWidth + 'px');
        }
        document.documentElement.classList.add('protection-popup-open');
        document.addEventListener('keydown', this.boundKeydown);

        // Prices are already correct from the Liquid render and kept current by
        // the variantChange subscriber; updatePrices() only writes if something
        // actually changed.
        this.updatePrices();

        const focusTarget = this.querySelector('[data-protection-confirm]');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }

      close() {
        this.removeAttribute('open');
        if (this.modal) {
          this.modal.classList.remove('is-open');
          this.modal.hidden = true;
        }
        document.documentElement.classList.remove('protection-popup-open');
        document.documentElement.style.removeProperty('--pp-scrollbar-width');
        document.removeEventListener('keydown', this.boundKeydown);
        this.resetSubmitButton();
      }

      onKeydown(event) {
        if (event.key === 'Escape') this.close();
      }

      get errorEl() {
        return this.querySelector('[data-protection-error]');
      }

      showError(message) {
        const el = this.errorEl;
        if (!el) return;
        el.textContent = message || this.dataset.addErrorMessage || 'Sorry, this item could not be added.';
        el.hidden = false;
      }

      clearError() {
        const el = this.errorEl;
        if (!el) return;
        el.textContent = '';
        el.hidden = true;
      }

      resetSubmitButton() {
        const form = this.activeForm;
        if (!form || !form.querySelector) return;
        const btn = form.querySelector('[type="submit"], [name="add"]');
        if (btn) {
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('loading');
        }
        const spinner = form.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }

      // Decline: add only the main product (no protection) via the same AJAX path
      // as confirm — no page reload; works for the product page and product cards.
      proceed() {
        const form = this.activeForm;
        this.close();
        if (!form) return;

        const formData = new FormData(form);
        const mainVariantId = formData.get('id');
        if (!mainVariantId) return;
        const quantity = parseInt(formData.get('quantity'), 10) || 1;

        const mainProperties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) mainProperties[match[1]] = value;
        }

        this.submitItems([{ id: mainVariantId, quantity: quantity, properties: mainProperties }]);
      }

      // Confirm: add the main product + selected protection variant together,
      // cross-linked with line item properties.
      addWithProtection() {
        const form = this.activeForm;
        const plan = this.selectedPlan;

        // No protection product configured -> behave like a normal add.
        if (!form || !plan) {
          this.proceed();
          return;
        }

        const formData = new FormData(form);
        const mainVariantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;

        // Preserve any existing main-product line item properties (e.g. fitment).
        const mainProperties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) mainProperties[match[1]] = value;
        }

        const protectionVariantId = plan.dataset.variantId;
        const pct = parseFloat(plan.dataset.percentage) || 0;
        const durationLabel = plan.dataset.durationLabel || '';
        const planPrice = this.formatMoney(Math.round((this.mainPrice * pct) / 100));

        const items = [
          {
            id: mainVariantId,
            quantity: quantity,
            properties: Object.assign({}, mainProperties, {
              'Protection plan': durationLabel,
              _protection_variant_id: protectionVariantId,
            }),
          },
          {
            // Warranty is always a single unit, regardless of how many of the
            // main product are added (parent qty 5 -> warranty qty still 1).
            id: protectionVariantId,
            quantity: 1,
            properties: {
              Protects: this.activeTitle || '',
              'For variant': this.currentVariant.title || '',
              Plan: durationLabel,
              'Protection price': planPrice,
              _main_variant_id: mainVariantId,
            },
          },
        ];

        this.submitItems(items);
      }

      submitItems(items) {
        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        // Show the loader on the "Protect my purchase" button until the response returns.
        const confirmButton = this.querySelector('[data-protection-confirm]');
        if (confirmButton) {
          confirmButton.classList.add('is-loading');
          confirmButton.setAttribute('aria-disabled', 'true');
          confirmButton.disabled = true;
        }

        const body = { items: items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'protection-popup',
                  message: response.message,
                  errors: response.errors || response.description,
                });
              }
              // Show a branded, in-popup error and keep the shopper on-site
              // (Shopify would otherwise drop a full-page POST onto its bare,
              // unbranded error page). Prefer Shopify's specific reason.
              this.showError(response.description || response.message);
              console.error('[protection-popup] add to cart failed:', response.description);
              return;
            }

            // The same product + plan can be added again; Shopify then merges the
            // identical warranty lines and the warranty quantity climbs above 1.
            // Warranty must always be exactly 1 unit, so clamp it back to 1 before
            // rendering. (The Cart Transform bundle only reflects the raw line, so
            // this has to be corrected on the raw line here.)
            const warrantyEntry = items.find((it) => it.properties && it.properties._main_variant_id);
            const warrantyVariantId = warrantyEntry ? String(warrantyEntry.id) : null;
            const addedWarranty =
              warrantyVariantId && Array.isArray(response.items)
                ? response.items.find(
                    (it) =>
                      String(it.variant_id || it.id) === warrantyVariantId &&
                      it.properties &&
                      it.properties._main_variant_id
                  )
                : null;

            if (cart && addedWarranty && addedWarranty.quantity > 1 && addedWarranty.key) {
              this.clampWarrantyToOne(addedWarranty.key, cart);
              return;
            }

            this.finishAdd(cart, response);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            if (confirmButton) {
              confirmButton.classList.remove('is-loading');
              confirmButton.removeAttribute('aria-disabled');
              confirmButton.disabled = false;
            }
            if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          });
      }

      // Close the popup and paint the updated cart from a cart response.
      finishAdd(cart, response) {
        this.close();

        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'protection-popup',
            cartData: response,
          });
        }

        cart.renderContents(response);
      }

      // Force a warranty line back to quantity 1 (see submitItems) and re-render.
      clampWarrantyToOne(lineKey, cart) {
        const body = {
          id: lineKey,
          quantity: 1,
          sections: cart.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        };
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);

        fetch(`${routes.cart_change_url}`, config)
          .then((response) => response.json())
          .then((response) => this.finishAdd(cart, response))
          .catch((error) => {
            console.error(error);
            this.close();
          });
      }

      // Standard Shopify money formatter (handles the common money_format placeholders).
      formatMoney(cents) {
        const format = this.moneyFormat;
        if (typeof cents === 'string') cents = cents.replace('.', '');

        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

        const formatWithDelimiters = (number, precision, thousands, decimal) => {
          precision = isNaN(precision) ? 2 : precision;
          thousands = thousands || ',';
          decimal = decimal || '.';
          if (isNaN(number) || number == null) return '0';

          number = (number / 100.0).toFixed(precision);
          const parts = number.split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
          const centsPart = parts[1] ? decimal + parts[1] : '';
          return dollars + centsPart;
        };

        let value = '';
        const match = format.match(placeholderRegex);
        const token = match ? match[1] : 'amount';

        switch (token) {
          case 'amount':
            value = formatWithDelimiters(cents, 2);
            break;
          case 'amount_no_decimals':
            value = formatWithDelimiters(cents, 0);
            break;
          case 'amount_with_comma_separator':
            value = formatWithDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = formatWithDelimiters(cents, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = formatWithDelimiters(cents, 2, "'", '.');
            break;
          default:
            value = formatWithDelimiters(cents, 2);
        }

        return format.replace(placeholderRegex, value);
      }
    }
  );
}

// ---------------------------------------------------------------------------
// Global Add-to-Cart interceptor.
// When the popup is enabled, an add-to-cart form anywhere on the site (product
// page, product cards, quick-add, featured collections) opens the popup for that
// product instead of adding directly — but only if that product offers a
// protection plan, i.e. it carries the `warranty_product` tag. Runs in the
// capture phase and stops propagation so it pre-empts the theme's own
// product-form handler AND the native form submit. When the popup is disabled,
// or the product is not warranty-eligible, forms submit as normal.
// ---------------------------------------------------------------------------
if (!window.__protectionPopupInterceptor) {
  window.__protectionPopupInterceptor = true;
  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      if ((form.getAttribute('action') || '').indexOf('/cart/add') === -1) return;

      // Opt-out: forms (or their wrapper) marked data-no-protection add to cart
      // directly without the popup — e.g. the custom wire form.
      if (form.closest('[data-no-protection]')) return;

      // Only products tagged `warranty_product` get the popup. The flag is
      // rendered onto the add-to-cart form (or its <product-form> host) next to
      // the data-atc-* attributes; anything without it adds to cart directly.
      if (!form.closest('[data-atc-warranty="true"]')) return;

      const popup = document.querySelector('protection-popup');
      if (!popup || !popup.enabled) return;

      event.preventDefault();
      event.stopPropagation();
      try {
        popup.openFor(form);
      } catch (e) {
        console.error('[protection-popup] openFor failed:', e);
      }
    },
    true
  );
}
